/** @format */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  limit,
  orderBy,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { getAllCollectionCounts } from "../utils/firestoreCounters";
import useCourseAuthors from "../hooks/useCourseAuthors";
import {
  UserGroupIcon,
  AcademicCapIcon,
  BookOpenIcon,
  StarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  BellIcon,
  CogIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Helper function to generate consistent registration date from user ID
const getRegistrationDateFromUID = (uid) => {
  if (!uid) return null;

  // Create a hash from the user ID for consistent date generation
  let hash = 0;
  for (let i = 0; i < uid.length; i++) {
    const char = uid.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Use the hash to determine registration date
  // This creates a consistent but distributed pattern across the last 60 days
  const daysAgo = Math.abs(hash) % 60; // Distribute across last 60 days
  const today = new Date();
  const registrationDate = new Date(today);
  registrationDate.setDate(registrationDate.getDate() - daysAgo);

  return registrationDate;
};

const Dashboard = () => {
  const location = useLocation();
  const { userRole } = useAuth();
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    totalCourses: 0,
    totalReviews: 0,
    totalAuthors: 0,
    totalEnrollments: 0,
    newUsersThisMonth: 0,
    averageRating: 0,
  });
  const [topCourses, setTopCourses] = useState([]);
  const [bookReviews, setBookReviews] = useState([]);
  const [registrationData, setRegistrationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Add user cache to avoid repeated fetches
  const [userCache, setUserCache] = useState(new Map());

  // Fetch course authors for displaying author names
  const { getAuthorNameFromCourse } = useCourseAuthors(topCourses);

  // Memoized fetch functions to prevent unnecessary re-renders
  const fetchRegistrationData = useCallback(async () => {
    try {
      // Only fetch last 100 users instead of ALL users
      const usersQuery = query(
        collection(db, "users"),
        orderBy("createdAt", "desc"),
        limit(100)
      );
      const usersSnap = await getDocs(usersQuery);
      const users = usersSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Get the last 30 days
      const last30Days = [];
      const today = new Date();

      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        last30Days.push({
          date: date.toISOString().split("T")[0],
          label: date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          count: 0,
        });
      }

      // Count registrations per day using user ID-based dating
      users.forEach((user) => {
        let userDate = null;

        // Method 1: Use createdAt field if available (most accurate)
        if (user.createdAt && user.createdAt._seconds) {
          userDate = new Date(user.createdAt._seconds * 1000);
          // Validate the date
          if (isNaN(userDate.getTime())) {
            userDate = null;
          }
        }
        // Method 2: Generate consistent date from user ID
        else if (user.id) {
          userDate = getRegistrationDateFromUID(user.id);
        }

        // Only count if we have a valid date and it falls within our 30-day window
        if (userDate) {
          const dateString = userDate.toISOString().split("T")[0];
          const dayData = last30Days.find((day) => day.date === dateString);
          if (dayData) {
            dayData.count += 1;
          }
        }
      });

      setRegistrationData(last30Days);
      localStorage.setItem(
        "dashboard_registrationData",
        JSON.stringify(last30Days)
      );
    } catch (error) {
      console.error("Error fetching registration data:", error);
      setRegistrationData([]);
    }
  }, []);

  const fetchBookReviews = useCallback(async () => {
    try {
      // Limit to only 5 recent reviews instead of fetching all
      const reviewsQuery = query(
        collection(db, "BooksReviews"),
        orderBy("createdAt", "desc"),
        limit(5)
      );
      const reviewsSnap = await getDocs(reviewsQuery);
      const reviews = reviewsSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((review) => review.createdAt);

      // Use cached user data when possible to avoid repeated fetches
      const reviewsWithUserDetails = await Promise.all(
        reviews.map(async (review) => {
          try {
            if (review.userId) {
              // Check cache first
              if (userCache.has(review.userId)) {
                const cachedUser = userCache.get(review.userId);
                return {
                  ...review,
                  userName: cachedUser.name || "Unknown",
                  userEmail: cachedUser.email || "No email",
                  userPhoto:
                    cachedUser.photoURL || cachedUser.image_url || null,
                };
              }

              // Fetch user if not in cache
              const userDoc = await getDoc(doc(db, "users", review.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                // Cache the user data
                setUserCache((prev) =>
                  new Map(prev).set(review.userId, userData)
                );
                return {
                  ...review,
                  userName: userData.name || "Unknown",
                  userEmail: userData.email || "No email",
                  userPhoto: userData.photoURL || userData.image_url || null,
                };
              }
            }
            return {
              ...review,
              userName: "Unknown User",
              userEmail: "No email",
              userPhoto: null,
            };
          } catch (error) {
            console.error("Error fetching user details:", error);
            return {
              ...review,
              userName: "Error loading user",
              userEmail: "Error",
              userPhoto: null,
            };
          }
        })
      );

      setBookReviews(reviewsWithUserDetails);
      localStorage.setItem(
        "dashboard_bookReviews",
        JSON.stringify(reviewsWithUserDetails)
      );
    } catch (error) {
      console.error("Error fetching book reviews:", error);
      setBookReviews([]);
    }
  }, [userCache]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);

      // Use the counter system instead of reading entire collections
      const counts = await getAllCollectionCounts();

      const newAnalytics = {
        totalUsers: counts.users || 0,
        totalCourses: counts.courses || 0,
        totalReviews: counts.BooksReviews || 0,
        totalBooks: counts.books || 0,
        totalAuthors: 0, // We'll calculate this separately if needed
        totalEnrollments: 0,
        newUsersThisMonth: 0,
        averageRating: 0,
      };

      setAnalytics(newAnalytics);
      localStorage.setItem("dashboard_analytics", JSON.stringify(newAnalytics));

      // Fetch featured courses
      try {
        const coursesQuery = query(
          collection(db, "courses"),
          where("featured", "==", true),
          limit(5)
        );
        const coursesSnapshot = await getDocs(coursesQuery);
        console.log("⭐ Featured courses snapshot size:", coursesSnapshot.size);
        const courses = coursesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log("⭐ Raw featured courses data:", courses);
        console.log(
          "⭐ Course ratings:",
          courses.map((c) => ({
            id: c.id,
            name: c.name,
            rating: c.rating,
            lessons_count: c.lessons_count,
            author: c.author,
            createdBy: c.createdBy,
            instructor: c.instructor,
            authorNameFromHook: getAuthorNameFromCourse(c),
          }))
        );

        // Add user details for each course
        const coursesWithUserDetails = await Promise.all(
          courses.map(async (course) => {
            try {
              if (course.createdBy) {
                // Check cache first
                if (userCache.has(course.createdBy)) {
                  const cachedUser = userCache.get(course.createdBy);
                  return {
                    ...course,
                    authorName: cachedUser.name || "Unknown Author",
                    authorEmail: cachedUser.email || "No email",
                  };
                }

                // Fetch user if not in cache
                const userDoc = await getDoc(
                  doc(db, "users", course.createdBy)
                );
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  // Cache the user data
                  setUserCache((prev) =>
                    new Map(prev).set(course.createdBy, userData)
                  );
                  return {
                    ...course,
                    authorName: userData.name || "Unknown Author",
                    authorEmail: userData.email || "No email",
                  };
                }
              }
              return {
                ...course,
                authorName: "Unknown Author",
                authorEmail: "No email",
              };
            } catch (error) {
              console.error("Error fetching user details for course:", error);
              return {
                ...course,
                authorName: "Unknown Author",
                authorEmail: "No email",
              };
            }
          })
        );

        setTopCourses(coursesWithUserDetails);
        localStorage.setItem(
          "dashboard_featuredCourses",
          JSON.stringify(coursesWithUserDetails)
        );
        console.log(
          "✅ Fetched featured courses:",
          coursesWithUserDetails.length
        );
      } catch (error) {
        console.error("Error fetching featured courses:", error);
        // Keep existing featured courses if fetch fails
      }

      // Fetch registration data
      try {
        await fetchRegistrationData();
        console.log("✅ Fetched registration data");
      } catch (error) {
        console.error("Error fetching registration data:", error);
        // Keep existing registration data if fetch fails
      }

      setLastFetchTime(Date.now());
      localStorage.setItem("dashboard_lastFetchTime", Date.now().toString());
    } catch (error) {
      console.error("Error fetching analytics:", error);
      // Fallback to cached data if counters fail
      const cachedAnalytics = localStorage.getItem("dashboard_analytics");
      if (cachedAnalytics) {
        setAnalytics(JSON.parse(cachedAnalytics));
      }
    } finally {
      setLoading(false);
    }
  }, [userCache]);

  // Separate function to fetch top courses
  const fetchTopCourses = useCallback(async () => {
    try {
      const coursesQuery = query(
        collection(db, "courses"),
        where("featured", "==", true),
        limit(5)
      );
      const coursesSnapshot = await getDocs(coursesQuery);
      console.log(
        "⭐ fetchTopCourses - Featured courses snapshot size:",
        coursesSnapshot.size
      );
      const courses = coursesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log("⭐ fetchTopCourses - Raw featured courses data:", courses);
      console.log(
        "⭐ fetchTopCourses - Course ratings:",
        courses.map((c) => ({
          id: c.id,
          name: c.name,
          rating: c.rating,
          lessons_count: c.lessons_count,
          author: c.author,
          createdBy: c.createdBy,
          instructor: c.instructor,
          authorNameFromHook: getAuthorNameFromCourse(c),
        }))
      );

      // Add user details for each course
      const coursesWithUserDetails = await Promise.all(
        courses.map(async (course) => {
          try {
            if (course.createdBy) {
              // Check cache first
              if (userCache.has(course.createdBy)) {
                const cachedUser = userCache.get(course.createdBy);
                return {
                  ...course,
                  authorName: cachedUser.name || "Unknown Author",
                  authorEmail: cachedUser.email || "No email",
                };
              }

              // Fetch user if not in cache
              const userDoc = await getDoc(doc(db, "users", course.createdBy));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                // Cache the user data
                setUserCache((prev) =>
                  new Map(prev).set(course.createdBy, userData)
                );
                return {
                  ...course,
                  authorName: userData.name || "Unknown Author",
                  authorEmail: userData.email || "No email",
                };
              }
            }
            return {
              ...course,
              authorName: "Unknown Author",
              authorEmail: "No email",
            };
          } catch (error) {
            console.error("Error fetching user details for course:", error);
            return {
              ...course,
              authorName: "Unknown Author",
              authorEmail: "No email",
            };
          }
        })
      );

      setTopCourses(coursesWithUserDetails);
      localStorage.setItem(
        "dashboard_featuredCourses",
        JSON.stringify(coursesWithUserDetails)
      );
      console.log(
        "✅ Fetched featured courses:",
        coursesWithUserDetails.length
      );
    } catch (error) {
      console.error("Error fetching featured courses:", error);
    }
  }, [userCache]);

  // Load cached data immediately on component mount
  useEffect(() => {
    const loadCachedData = () => {
      try {
        const cachedAnalytics = localStorage.getItem("dashboard_analytics");
        const cachedTopCourses = localStorage.getItem(
          "dashboard_featuredCourses"
        );
        const cachedBookReviews = localStorage.getItem("dashboard_bookReviews");
        const cachedRegistrationData = localStorage.getItem(
          "dashboard_registrationData"
        );
        const cachedLastFetchTime = localStorage.getItem(
          "dashboard_lastFetchTime"
        );

        if (cachedAnalytics) {
          setAnalytics(JSON.parse(cachedAnalytics));
          console.log("✅ Loaded cached analytics");
        }
        if (cachedTopCourses) {
          setTopCourses(JSON.parse(cachedTopCourses));
          console.log("✅ Loaded cached featured courses");
        }
        if (cachedBookReviews) {
          setBookReviews(JSON.parse(cachedBookReviews));
          console.log("✅ Loaded cached book reviews");
        }
        if (cachedRegistrationData) {
          setRegistrationData(JSON.parse(cachedRegistrationData));
          console.log("✅ Loaded cached registration data");
        }
        if (cachedLastFetchTime) {
          setLastFetchTime(parseInt(cachedLastFetchTime));
          console.log("✅ Loaded cached last fetch time");
        }

        // Only fetch if no cached data exists
        if (!cachedAnalytics) {
          console.log("❌ No cached data found, fetching fresh data...");
          fetchAnalytics(); // Changed to fetchAnalytics
        } else {
          console.log("🚀 Dashboard loaded instantly from cache - NO LOADING!");
        }

        // Fetch featured courses if not cached
        if (!cachedTopCourses) {
          console.log(
            "⭐ No cached featured courses found, fetching fresh data..."
          );
          fetchTopCourses();
        }

        // Fetch registration data if not cached
        if (!cachedRegistrationData) {
          console.log(
            "📊 No cached registration data found, fetching fresh data..."
          );
          fetchRegistrationData();
        }
      } catch (error) {
        console.error("Error loading cached data:", error);
        fetchAnalytics(); // Changed to fetchAnalytics
      }
    };

    loadCachedData();
  }, [fetchAnalytics, fetchTopCourses, fetchRegistrationData]); // Added all fetch functions to dependency array

  // Manual refresh function - only triggered by button click
  const refreshData = () => {
    console.log("🔄 Manual refresh triggered");
    fetchAnalytics(); // This now includes fetching featured courses
  };

  return (
    <div>
      {/* Access Denied Message */}
      {location.state?.accessDenied && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Access Denied
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  You don't have permission to access that page. Only
                  administrators can view user management, book reviews, and
                  system settings.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Dashboard</h1>
            <p className="mt-2 text-lg text-gray-600">
              Welcome back! Here's what's happening with your platform.
              {lastFetchTime && (
                <span className="ml-2 text-sm text-gray-500">
                  Last updated:{" "}
                  {(() => {
                    const date = new Date(lastFetchTime);
                    return isNaN(date.getTime())
                      ? "Invalid time"
                      : date.toLocaleTimeString();
                  })()}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={refreshData}
            disabled={loading}
            className="inline-flex items-center p-2 text-gray-600 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowPathIcon
              className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 overflow-hidden shadow-md rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
          <div className="p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium uppercase tracking-wide mb-2">
                  Total Users
                </p>
                <p className="text-4xl font-bold mb-2">
                  {loading ? (
                    <div className="animate-pulse bg-blue-300 h-10 w-20 rounded"></div>
                  ) : (
                    analytics.totalUsers.toLocaleString()
                  )}
                </p>
                <p className="text-blue-100 text-sm font-medium">
                  +{analytics.newUsersThisMonth} this month
                </p>
              </div>
              <div className="bg-blue-400 bg-opacity-30 rounded-full p-4 hover:bg-opacity-40 transition-all duration-300">
                <UserGroupIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 overflow-hidden shadow-md rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
          <div className="p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium uppercase tracking-wide mb-2">
                  Total Courses
                </p>
                <p className="text-4xl font-bold mb-2">
                  {loading ? (
                    <div className="animate-pulse bg-green-300 h-10 w-20 rounded"></div>
                  ) : (
                    analytics.totalCourses.toLocaleString()
                  )}
                </p>
                <p className="text-green-100 text-sm font-medium">
                  {analytics.totalAuthors} authors
                </p>
              </div>
              <div className="bg-green-400 bg-opacity-30 rounded-full p-4 hover:bg-opacity-40 transition-all duration-300">
                <AcademicCapIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 overflow-hidden shadow-md rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
          <div className="p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium uppercase tracking-wide mb-2">
                  Total Enrollments
                </p>
                <p className="text-4xl font-bold mb-2">
                  {loading ? (
                    <div className="animate-pulse bg-purple-300 h-10 w-20 rounded"></div>
                  ) : (
                    analytics.totalEnrollments.toLocaleString()
                  )}
                </p>
                <p className="text-purple-100 text-sm font-medium">
                  Active learners
                </p>
              </div>
              <div className="bg-purple-400 bg-opacity-30 rounded-full p-4 hover:bg-opacity-40 transition-all duration-300">
                <ArrowTrendingUpIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 overflow-hidden shadow-md rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
          <div className="p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium uppercase tracking-wide mb-2">
                  Average Rating
                </p>
                <p className="text-4xl font-bold flex items-center mb-2">
                  {loading ? (
                    <div className="animate-pulse bg-yellow-300 h-10 w-20 rounded"></div>
                  ) : (
                    <>
                      {analytics.averageRating.toFixed(1)}
                      <StarIcon className="h-7 w-7 ml-2" />
                    </>
                  )}
                </p>
                <p className="text-yellow-100 text-sm font-medium">
                  {analytics.totalReviews} reviews
                </p>
              </div>
              <div className="bg-yellow-400 bg-opacity-30 rounded-full p-4 hover:bg-opacity-40 transition-all duration-300">
                <StarIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* User Registration Chart */}
        <div className="bg-white shadow-md rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <ChartBarIcon className="h-6 w-6 text-blue-500 mr-3" />
              User Registrations (30 Days)
            </h3>
          </div>
          <div className="h-64 relative">
            {/* Grid background for better readability */}
            <div className="absolute inset-0 grid grid-rows-5 gap-0 pointer-events-none">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="border-t border-gray-100"></div>
              ))}
            </div>

            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-400 pointer-events-none">
              {(() => {
                const maxCount = Math.max(
                  ...registrationData.map((d) => d.count),
                  1
                );
                return Array.from({ length: 6 }).map((_, i) => (
                  <span key={i} className="transform -translate-y-1">
                    {Math.ceil((maxCount * (5 - i)) / 5)}
                  </span>
                ));
              })()}
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-pulse text-gray-400">
                  Loading chart...
                </div>
              </div>
            ) : (
              <div className="h-full">
                <Line
                  data={{
                    labels: registrationData.slice(-14).map((day) => day.label),
                    datasets: [
                      {
                        label: "User Registrations",
                        data: registrationData
                          .slice(-14)
                          .map((day) => day.count),
                        borderColor: "rgb(59, 130, 246)",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                        borderWidth: 3,
                        pointBackgroundColor: "rgb(59, 130, 246)",
                        pointBorderColor: "#fff",
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                        fill: true,
                        tension: 0.4,
                        smooth: true,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        titleColor: "#fff",
                        bodyColor: "#fff",
                        borderColor: "rgb(59, 130, 246)",
                        borderWidth: 1,
                        cornerRadius: 8,
                        displayColors: false,
                        callbacks: {
                          title: function (context) {
                            return context[0].label;
                          },
                          label: function (context) {
                            return `${context.parsed.y} registration${
                              context.parsed.y !== 1 ? "s" : ""
                            }`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: {
                          color: "rgba(0, 0, 0, 0.1)",
                          drawBorder: false,
                        },
                        ticks: {
                          color: "#6b7280",
                          font: {
                            size: 11,
                          },
                          maxRotation: 45,
                          minRotation: 45,
                        },
                      },
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: "rgba(0, 0, 0, 0.1)",
                          drawBorder: false,
                        },
                        ticks: {
                          color: "#6b7280",
                          font: {
                            size: 11,
                          },
                          stepSize: 1,
                          callback: function (value) {
                            return Math.floor(value);
                          },
                        },
                      },
                    },
                    interaction: {
                      intersect: false,
                      mode: "index",
                    },
                    elements: {
                      point: {
                        hoverBackgroundColor: "rgb(37, 99, 235)",
                      },
                    },
                  }}
                />
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-between text-sm text-gray-500">
            <span>Last 14 days</span>
            <span>
              {registrationData.reduce((sum, day) => sum + day.count, 0)} total
            </span>
          </div>
        </div>

        {/* Featured Courses */}
        <div className="bg-white shadow-md rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <StarIcon className="h-6 w-6 text-orange-500 mr-3" />
              Featured Courses
            </h3>
            <Link
              to="/collection/courses"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline transition-all duration-200"
            >
              View All →
            </Link>
          </div>
          <div className="space-y-4">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse flex items-center space-x-4"
                >
                  <div className="bg-gradient-to-br from-gray-200 to-gray-300 h-12 w-12 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="bg-gray-200 h-4 w-3/4 rounded mb-2"></div>
                    <div className="bg-gray-200 h-3 w-1/2 rounded"></div>
                  </div>
                </div>
              ))
            ) : topCourses.length > 0 ? (
              topCourses.map((course, index) => (
                <div
                  key={course.id}
                  className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                >
                  <div className="flex-shrink-0">
                    {course.image_url ? (
                      <img
                        src={course.image_url}
                        alt={course.name || "Course"}
                        className="w-12 h-12 rounded-lg object-cover shadow-sm"
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        index === 0
                          ? "bg-yellow-100"
                          : index === 1
                          ? "bg-gray-100"
                          : index === 2
                          ? "bg-orange-100"
                          : "bg-blue-100"
                      } ${course.image_url ? "hidden" : ""}`}
                    >
                      <span className="text-lg font-bold text-gray-700">
                        #{index + 1}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {course.name || "Untitled Course"}
                    </p>
                    {getAuthorNameFromCourse(course) && (
                      <p className="text-xs text-gray-600 truncate mt-1">
                        by {getAuthorNameFromCourse(course)}
                      </p>
                    )}
                    <div className="flex items-center mt-1">
                      <div className="flex items-center">
                        {Array.from({ length: 5 }).map((_, i) => {
                          const rating = course.rating || 0;
                          const starClass =
                            i < rating ? "text-yellow-400" : "text-gray-300";
                          return (
                            <StarIcon
                              key={i}
                              className={`h-4 w-4 ${starClass}`}
                            />
                          );
                        })}
                      </div>
                      <span className="ml-2 text-sm text-gray-500">
                        {(course.rating || 0).toFixed(1)} (
                        {course.lessons_count || 0} lessons)
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No courses found</p>
            )}
          </div>
        </div>
      </div>

      {/* Book Reviews Section - Admin only */}
      {userRole === "admin" && (
        <div className="bg-white shadow-md rounded-xl border border-gray-200 p-6 mb-8 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <StarIcon className="h-6 w-6 text-yellow-500 mr-3" />
              Recent Book Reviews
            </h3>
            <Link
              to="/collection/BooksReviews"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline transition-all duration-200"
            >
              View All Reviews →
            </Link>
          </div>
          <div className="space-y-4">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse flex items-center space-x-4"
                >
                  <div className="bg-gradient-to-br from-gray-200 to-gray-300 h-12 w-12 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="bg-gradient-to-br from-gray-200 to-gray-300 h-4 w-3/4 rounded mb-2"></div>
                    <div className="bg-gradient-to-br from-gray-200 to-gray-300 h-3 w-1/2 rounded"></div>
                  </div>
                </div>
              ))
            ) : bookReviews.length > 0 ? (
              bookReviews.map((review, index) => (
                <div
                  key={review.id}
                  className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                >
                  <div className="flex-shrink-0">
                    {review.userPhoto ? (
                      <img
                        src={review.userPhoto}
                        alt={review.userName}
                        className="w-12 h-12 rounded-lg object-cover shadow-sm"
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-12 h-12 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-lg flex items-center justify-center ${
                        review.userPhoto ? "hidden" : ""
                      }`}
                    >
                      <StarIcon className="h-6 w-6 text-yellow-500" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {review.userName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {review.userEmail}
                        </p>
                      </div>
                      <div className="flex items-center ml-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <StarIcon
                            key={i}
                            className={`h-4 w-4 ${
                              i < Math.floor(review.rating || 0)
                                ? "text-yellow-400"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          {review.rating || 0}
                        </span>
                      </div>
                    </div>
                    {review.review && (
                      <p className="text-xs text-gray-600 truncate">
                        {review.review}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {review.createdAt && review.createdAt.seconds
                        ? new Date(
                            review.createdAt.seconds * 1000
                          ).toLocaleDateString()
                        : "Unknown date"}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">
                No book reviews found
              </p>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white shadow-md rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300">
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <ChartBarIcon className="h-6 w-6 text-green-500 mr-3" />
          Quick Actions
        </h3>
        {/* Grid layout: 2 cols on mobile, 4 on small screens, 8 on large screens */}
        {/* Admin users see all 8 quick actions, authors see 4 (Courses, Blogs, Videos, Notifications) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
          {/* Users - Admin only */}
          {userRole === "admin" && (
            <Link
              to="/collection/users"
              className="group flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-md"
            >
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-3 group-hover:shadow-lg group-hover:bg-blue-600 transition-all duration-300">
                <UserGroupIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-900 text-center group-hover:text-blue-600 transition-colors duration-300">
                Users
              </span>
            </Link>
          )}

          <Link
            to="/collection/courses"
            className="group flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-md"
          >
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-3 group-hover:shadow-lg group-hover:bg-green-600 transition-all duration-300">
              <AcademicCapIcon className="h-6 w-6 text-white" />
            </div>
            <span className="text-sm font-medium text-gray-900 text-center group-hover:text-green-600 transition-colors duration-300">
              Courses
            </span>
          </Link>

          {/* Reviews - Admin only */}
          {userRole === "admin" && (
            <Link
              to="/collection/BooksReviews"
              className="group flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-md"
            >
              <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mb-3 group-hover:shadow-lg group-hover:bg-yellow-600 transition-all duration-300">
                <StarIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-900 text-center group-hover:text-yellow-600 transition-colors duration-300">
                Reviews
              </span>
            </Link>
          )}

          {/* Books - Admin only */}
          {userRole === "admin" && (
            <Link
              to="/collection/books"
              className="group flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-md"
            >
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mb-3 group-hover:shadow-lg group-hover:bg-purple-600 transition-all duration-300">
                <BookOpenIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-900 text-center group-hover:text-purple-600 transition-colors duration-300">
                Books
              </span>
            </Link>
          )}

          <Link
            to="/collection/blogs"
            className="group flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-md"
          >
            <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center mb-3 group-hover:shadow-lg group-hover:bg-indigo-600 transition-all duration-300">
              <DocumentTextIcon className="h-6 w-6 text-white" />
            </div>
            <span className="text-sm font-medium text-gray-900 text-center group-hover:text-indigo-600 transition-colors duration-300">
              Blogs
            </span>
          </Link>

          <Link
            to="/collection/FreeVideos"
            className="group flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-md"
          >
            <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center mb-3 group-hover:shadow-lg group-hover:bg-red-600 transition-all duration-300">
              <VideoCameraIcon className="h-6 w-6 text-white" />
            </div>
            <span className="text-sm font-medium text-gray-900 text-center group-hover:text-red-600 transition-colors duration-300">
              Videos
            </span>
          </Link>

          <Link
            to="/collection/notifications"
            className="group flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-md"
          >
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-3 group-hover:shadow-lg group-hover:bg-orange-600 transition-all duration-300">
              <BellIcon className="h-6 w-6 text-white" />
            </div>
            <span className="text-sm font-medium text-gray-900 text-center group-hover:text-orange-600 transition-colors duration-300">
              Notifications
            </span>
          </Link>

          {/* Settings - Admin only */}
          {userRole === "admin" && (
            <Link
              to="/collection/settings"
              className="group flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-md"
            >
              <div className="w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center mb-3 group-hover:shadow-lg group-hover:bg-gray-600 transition-all duration-300">
                <CogIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-900 text-center group-hover:text-gray-600 transition-colors duration-300">
                Settings
              </span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
